/**
 * B2B 配送希望日: leadtime・13:00 締め・日曜・土曜・休業日・調整日に基づく最短日算出とチェックアウト時の再検証。
   * 休業日は受注・出荷・着荷指定・リードタイムすべて不可（土曜も休業日登録で除外）。調整日は受注のみ不可（出荷は継続）。
   * 土曜は未登録なら着荷指定可だが出荷日にならない。日曜は常に不可。
 * ページ読み込み時は現在日時で保存済み日付を検証し、古い最短日は自動更新する。
 * チェックアウト時のみ delivery_set_at を基準に再検証する（同一セッション内の締め時間跨ぎ対策）。
 */

(function () {
  const DATE_ATTRIBUTE_KEY_DEFAULT = 'delivery_date';
  const TIME_ATTRIBUTE_KEY_DEFAULT = 'delivery_time';
  const SET_AT_ATTRIBUTE_KEY_DEFAULT = 'delivery_set_at';
  const TIME_NONE_DEFAULT = '指定なし';
  const COMPANY_LOCATION_ID_ATTRIBUTE_KEY = 'cart_company_location_id';
  const NAVISION_ATTRIBUTE_KEY = 'NAVISION';
  const COMPANY_LOCATION_ID_INPUT_ID = 'cart-attributes-cart_company_location_id';
  const NAVISION_INPUT_ID = 'cart-attributes-cart_NAVISION';
  const COMPANY_LOCATION_ATTRS_ID = 'pds-company-location-attributes';
  const CART_ATTRIBUTE_UPDATE_FAILED_MESSAGE = 'カート情報の更新に失敗しました。もう一度お試しください。';
  const COMPANY_LOCATION_REQUIRED_MESSAGE =
    '拠点情報が設定されていないため、お手続きを続行できません。ページを再読み込みするか、拠点の紐付けをご確認ください。';

  /** @type {Promise<boolean>} */
  let attributeUpdateChain = Promise.resolve();

  /**
   * @param {string} ymd
   * @returns {Date}
   */
  function parseYmd(ymd) {
    const [y, m, d] = ymd.split('-').map((part) => parseInt(part, 10));
    return new Date(y, m - 1, d);
  }

  /**
   * @param {Date} date
   * @returns {string}
   */
  function formatYmd(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /**
   * @param {string} timeZone
   * @returns {{ year: number, month: number, day: number, hour: number, minute: number, second: number }}
   */
  function getZonedNowParts(timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(new Date());

    const get = (type) => parseInt(parts.find((p) => p.type === type).value, 10);
    return {
      year: get('year'),
      month: get('month'),
      day: get('day'),
      hour: get('hour'),
      minute: get('minute'),
      second: get('second'),
    };
  }

  /**
   * @param {string} timeZone
   * @returns {Date}
   */
  function getTodayInTimeZone(timeZone) {
    const { year, month, day } = getZonedNowParts(timeZone);
    return new Date(year, month - 1, day);
  }

  /**
   * 13:00（JST）以降は締め時間を過ぎた扱い。
   * @param {number} cutoffHour
   * @param {string} timeZone
   * @returns {boolean}
   */
  function isPastCutoff(cutoffHour, timeZone) {
    const { hour } = getZonedNowParts(timeZone);
    return hour >= cutoffHour;
  }

  /**
   * カートで配送指定を保存した日時（JST・YYYY-MM-DDTHH:mm:ss）。
   * @param {string} timeZone
   * @returns {string}
   */
  function formatSetAtNow(timeZone) {
    const parts = getZonedNowParts(timeZone);
    const month = String(parts.month).padStart(2, '0');
    const day = String(parts.day).padStart(2, '0');
    const hour = String(parts.hour).padStart(2, '0');
    const minute = String(parts.minute).padStart(2, '0');
    const second = String(parts.second).padStart(2, '0');
    return `${parts.year}-${month}-${day}T${hour}:${minute}:${second}`;
  }

  /**
   * @param {string} raw
   * @returns {{ year: number, month: number, day: number, hour: number, minute: number, second: number }|null}
   */
  function parseSetAt(raw) {
    const value = String(raw || '').trim();
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
    if (!match) return null;

    return {
      year: parseInt(match[1], 10),
      month: parseInt(match[2], 10),
      day: parseInt(match[3], 10),
      hour: parseInt(match[4], 10),
      minute: parseInt(match[5], 10),
      second: parseInt(match[6], 10),
    };
  }

  /**
   * 基準日時を返す。UI・ページ読み込み時は current（現在日時）、チェックアウト再検証は setAt。
   * @param {object} config
   * @param {'current'|'setAt'} [referenceMode='current']
   * @returns {{ today: Date, pastCutoff: boolean }}
   */
  function getReferenceContext(config, referenceMode = 'current') {
    if (referenceMode === 'setAt') {
      const parsed = parseSetAt(config.selectedSetAt);
      if (parsed) {
        return {
          today: new Date(parsed.year, parsed.month - 1, parsed.day),
          pastCutoff: parsed.hour >= (config.cutoffHour ?? 13),
        };
      }
    }

    return {
      today: getTodayInTimeZone(config.timeZone),
      pastCutoff: isPastCutoff(config.cutoffHour, config.timeZone),
    };
  }

  /**
   * @param {string} raw
   * @returns {string[]}
   */
  /**
   * @param {string} part
   * @returns {string|null}
   */
  function normalizeHolidayDate(part) {
    const value = String(part).trim().replace(/\r/g, '');
    if (!value) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

    const slashMatch = value.match(/^(\d{4})[/.](\d{1,2})[/.](\d{1,2})$/);
    if (slashMatch) {
      const month = String(slashMatch[2]).padStart(2, '0');
      const day = String(slashMatch[3]).padStart(2, '0');
      return `${slashMatch[1]}-${month}-${day}`;
    }

    return null;
  }

  /**
   * @param {string|string[]} raw
   * @returns {string[]}
   */
  function parseHolidays(raw) {
    if (Array.isArray(raw)) {
      return raw.map(normalizeHolidayDate).filter(Boolean);
    }

    return String(raw || '')
      .split(/[\n,\r;]+/)
      .map(normalizeHolidayDate)
      .filter(Boolean);
  }

  /**
   * @param {object} config
   * @returns {Set<string>}
   */
  function getHolidaySet(config) {
    const merged = [...parseHolidays(config.holidays), ...parseHolidays(config.holidaysRaw)];
    return new Set(merged);
  }

  /**
   * @param {object} config
   * @returns {Set<string>}
   */
  function getAdjustmentDaySet(config) {
    const merged = [
      ...parseHolidays(config.adjustmentDays),
      ...parseHolidays(config.adjustmentDaysRaw),
    ];
    return new Set(merged);
  }

  /**
   * カレンダーから選択した日付を hidden select に反映する（該当 option が無ければ追加）。
   * @param {HTMLSelectElement} dateSelect
   * @param {string} ymd
   * @returns {boolean}
   */
  function setDateSelectValue(dateSelect, ymd) {
    if (!dateSelect || !ymd) return false;

    const date = parseYmd(ymd);
    if (Number.isNaN(date.getTime())) return false;

    let option = Array.from(dateSelect.options).find((opt) => opt.value === ymd);
    if (!option) {
      option = document.createElement('option');
      option.value = ymd;
      option.textContent = formatDisplayDate(date);
      dateSelect.appendChild(option);
    }

    Array.from(dateSelect.options).forEach((opt) => {
      opt.selected = opt === option;
    });
    dateSelect.value = ymd;

    return dateSelect.value === ymd;
  }

  /**
   * @param {Date} date
   * @returns {string}
   */
  function formatDisplayDate(date) {
    const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日（${weekdays[date.getDay()]}）`;
  }

  /**
   * 日曜・休業日を除いた選択肢のみをセレクトに出力する。
   * @param {HTMLSelectElement} dateSelect
   * @param {object} config
   */
  function populateDateSelect(dateSelect, config) {
    const placeholder = config.strings.datePlaceholder || '選択してください';
    const preferredValue = dateSelect.value || config.selectedDate || '';
    const { earliest, max } = getSelectableRange(config);
    const holidays = getHolidaySet(config);

    dateSelect.innerHTML = '';

    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.textContent = placeholder;
    placeholderOption.disabled = true;
    dateSelect.appendChild(placeholderOption);

    let matchedPreferred = false;
    let cursor = new Date(earliest.getTime());

    while (cursor <= max) {
      if (isDeliverable(cursor, holidays)) {
        const ymd = formatYmd(cursor);
        const option = document.createElement('option');
        option.value = ymd;
        option.textContent = formatDisplayDate(cursor);

        if (preferredValue === ymd) {
          option.selected = true;
          matchedPreferred = true;
        }

        dateSelect.appendChild(option);
      }

      cursor = addCalendarDays(cursor, 1);
    }

    placeholderOption.selected = !matchedPreferred;
    if (!matchedPreferred) {
      dateSelect.value = '';
    }
  }

  /**
   * @param {string} raw
   * @returns {string[]}
   */
  function parseTimeSlots(raw) {
    return String(raw || '')
      .split(/\n+/)
      .map((part) => part.trim())
      .filter(Boolean);
  }

  /**
   * @param {object} config
   * @returns {string[]}
   */
  function getTimeSlots(config) {
    if (Array.isArray(config.timeSlots)) return config.timeSlots;
    return parseTimeSlots(config.timeSlotsRaw);
  }

  /**
   * @param {string} raw
   * @returns {number|null}
   */
  function parseLeadtimeDays(raw) {
    const match = String(raw || '')
      .trim()
      .match(/^(\d+)D$/i);
    return match ? parseInt(match[1], 10) : null;
  }

  /**
   * 5D のみ、出荷日カウント完了日の翌着荷可能日を最短着日とする（実質 6 日目）。
   * @param {number} leadDays
   * @returns {boolean}
   */
  function usesPostShippingDeliveryDay(leadDays) {
    return leadDays === 5;
  }

  /**
   * 休業日（設定した日付・日曜）。土曜を休業日に登録した場合もここで除外される。
   * @param {Date} date
   * @param {Set<string>} holidays
   * @returns {boolean}
   */
  function isClosedDay(date, holidays) {
    if (holidays.has(formatYmd(date))) return true;
    return date.getDay() === 0;
  }

  /**
   * 着荷指定可能日（休業日・日曜除外。未登録の土曜は指定可）。
   * @param {Date} date
   * @param {Set<string>} holidays
   * @returns {boolean}
   */
  function isDeliverable(date, holidays) {
    return !isClosedDay(date, holidays);
  }

  /**
   * 出荷日（休業日・日曜・土曜除外。調整日は出荷可）。
   * @param {Date} date
   * @param {Set<string>} holidays
   * @returns {boolean}
   */
  function isShippingDay(date, holidays) {
    if (isClosedDay(date, holidays)) return false;
    return date.getDay() !== 6;
  }

  /**
   * 受注処理可能日（休業日・日曜・土曜・調整日除外）。
   * @param {Date} date
   * @param {Set<string>} holidays
   * @param {Set<string>} adjustmentDays
   * @returns {boolean}
   */
  function isOrderProcessingDay(date, holidays, adjustmentDays) {
    if (!isShippingDay(date, holidays)) return false;
    return !adjustmentDays.has(formatYmd(date));
  }

  /**
   * @param {Date} date
   * @param {Set<string>} holidays
   * @param {Set<string>} adjustmentDays
   * @returns {Date}
   */
  function advanceToNextOrderProcessingDay(date, holidays, adjustmentDays) {
    let next = addCalendarDays(date, 1);
    while (!isOrderProcessingDay(next, holidays, adjustmentDays)) {
      next = addCalendarDays(next, 1);
    }
    return next;
  }

  /**
   * 受注処理日を返す。土日・休業日・調整日は翌受注処理日へスライドする。
   * 13時以降の締めは、注文日が受注処理可能日の場合のみ翌受注処理日へスライドする。
   * @param {Date} today
   * @param {boolean} pastCutoff
   * @param {Set<string>} holidays
   * @param {Set<string>} adjustmentDays
   * @returns {Date}
   */
  function getEffectiveOrderDay(today, pastCutoff, holidays, adjustmentDays) {
    const onOrderProcessingDay = isOrderProcessingDay(today, holidays, adjustmentDays);
    let orderDay = new Date(today.getTime());

    if (!onOrderProcessingDay) {
      while (!isOrderProcessingDay(orderDay, holidays, adjustmentDays)) {
        orderDay = addCalendarDays(orderDay, 1);
      }
      return orderDay;
    }

    if (pastCutoff) {
      orderDay = advanceToNextOrderProcessingDay(orderDay, holidays, adjustmentDays);
    }

    return orderDay;
  }

  /**
   * 受注処理日の翌日から最初の出荷日を返す。
   * @param {Date} orderProcessingDay
   * @param {Set<string>} holidays
   * @returns {Date}
   */
  function getFirstShippingDay(orderProcessingDay, holidays) {
    let cursor = addCalendarDays(orderProcessingDay, 1);

    while (!isShippingDay(cursor, holidays)) {
      cursor = addCalendarDays(cursor, 1);
    }

    return cursor;
  }

  /**
   * 初回出荷日から leadDays 着荷可能日後の最短着日を返す。5D は着荷可能日 5 日目の翌着荷可能日。
   * @param {Date} firstShippingDay
   * @param {number} leadDays
   * @param {Set<string>} holidays
   * @returns {Date}
   */
  function getEarliestAfterLead(firstShippingDay, leadDays, holidays) {
    const lastLeadDay = addDeliveryLeadDays(firstShippingDay, leadDays, holidays);

    if (!usesPostShippingDeliveryDay(leadDays)) {
      return lastLeadDay;
    }

    let deliveryDay = addCalendarDays(lastLeadDay, 1);
    while (!isDeliverable(deliveryDay, holidays)) {
      deliveryDay = addCalendarDays(deliveryDay, 1);
    }

    return deliveryDay;
  }

  /**
   * 初回出荷日の翌日から leadDays 着荷可能日後（日曜・休業日除外、土曜・調整日可）の日付を返す。
   * 出荷後の輸送日（1D・2D…）のカウントで、着荷可能な土曜も 1 日として数える。
   * @param {Date} firstShippingDay
   * @param {number} leadDays
   * @param {Set<string>} holidays
   * @returns {Date}
   */
  function addDeliveryLeadDays(firstShippingDay, leadDays, holidays) {
    let count = 0;
    let cursor = new Date(firstShippingDay.getTime());

    while (count < leadDays) {
      cursor = addCalendarDays(cursor, 1);
      if (isDeliverable(cursor, holidays)) {
        count += 1;
      }
    }

    return cursor;
  }

  /**
   * @param {Date} date
   * @param {number} days
   * @returns {Date}
   */
  function addCalendarDays(date, days) {
    const next = new Date(date.getTime());
    next.setDate(next.getDate() + days);
    return next;
  }

  /**
   * @param {object} config
   * @param {'current'|'setAt'} [referenceMode='current']
   * @returns {Date}
   */
  function getEarliestDeliveryDate(config, referenceMode = 'current') {
    const leadDays = parseLeadtimeDays(config.leadtime);
    if (!(leadDays > 0)) {
      return getTodayInTimeZone(config.timeZone);
    }

    const holidays = getHolidaySet(config);
    const adjustmentDays = getAdjustmentDaySet(config);
    const { today, pastCutoff } = getReferenceContext(config, referenceMode);
    const orderProcessingDay = getEffectiveOrderDay(today, pastCutoff, holidays, adjustmentDays);
    const firstShippingDay = getFirstShippingDay(orderProcessingDay, holidays);

    return getEarliestAfterLead(firstShippingDay, leadDays, holidays);
  }

  /**
   * @param {object} config
   * @param {'current'|'setAt'} [referenceMode='current']
   * @returns {{ earliest: Date, max: Date }}
   */
  function getSelectableRange(config, referenceMode = 'current') {
    const earliest = getEarliestDeliveryDate(config, referenceMode);
    const max = addCalendarDays(earliest, config.maxSelectableDays ?? 7);
    return { earliest, max };
  }

  /**
   * @param {string} ymd
   * @param {object} config
   * @param {'current'|'setAt'} [referenceMode='current']
   * @returns {string|null}
   */
  function validateSelectedDate(ymd, config, referenceMode = 'current') {
    if (!ymd) return config.strings.required;

    const selected = parseYmd(ymd);
    if (Number.isNaN(selected.getTime())) return config.strings.notDeliverable;

    const holidays = getHolidaySet(config);
    if (!isDeliverable(selected, holidays)) return config.strings.notDeliverable;

    const earliest = getEarliestDeliveryDate(config, referenceMode);
    if (selected < earliest) {
      return config.strings.tooEarly.replace('[date]', formatYmd(earliest));
    }

    const { max } = getSelectableRange(config, referenceMode);
    if (selected > max) return config.strings.notDeliverable;

    return null;
  }

  /**
   * @param {string} value
   * @param {object} config
   * @returns {string|null}
   */
  /**
   * @param {object} config
   * @param {number} [itemCount]
   * @returns {boolean}
   */
  function resolveShowTimeSlot(config, itemCount) {
    const mode = String(config.deliverytimeMetafield || '').trim();
    const max = config.timeSlotItemCountMax ?? 5;
    if (mode === 'YT') return true;
    if (mode === 'NULL') {
      if (typeof itemCount !== 'number' || Number.isNaN(itemCount)) {
        return config.showTimeSlot === true;
      }
      return itemCount < max;
    }
    return false;
  }

  /**
   * @param {object} config
   * @returns {boolean}
   */
  function isTimeSlotRequired(config) {
    return resolveShowTimeSlot(config);
  }

  /**
   * @param {object} config
   * @returns {string}
   */
  function getTimeNoneValue(config) {
    return config.timeNoneValue || TIME_NONE_DEFAULT;
  }

  /**
   * @param {string} value
   * @param {object} config
   * @returns {string|null}
   */
  function validateSelectedTime(value, config) {
    if (!isTimeSlotRequired(config)) return null;
    if (!value) return config.strings.timeRequired;

    const slots = getTimeSlots(config);
    if (!slots.length || !slots.includes(value)) return config.strings.timeInvalid;

    return null;
  }

  /**
   * @param {HTMLSelectElement|null} dateSelect
   * @param {object} config
   * @param {'current'|'setAt'} [referenceMode='current']
   * @param {boolean} [forceEarliest=false]
   * @returns {string}
   */
  function ensureEarliestDate(dateSelect, config, referenceMode = 'current', forceEarliest = false) {
    if (!dateSelect) return '';

    populateDateSelect(dateSelect, config);

    if (
      !forceEarliest &&
      dateSelect.value &&
      !validateSelectedDate(dateSelect.value, config, referenceMode)
    ) {
      return dateSelect.value;
    }

    const firstOption = dateSelect.querySelector('option[value]:not([value=""])');
    if (firstOption) {
      dateSelect.value = firstOption.value;
      return firstOption.value;
    }

    return '';
  }

  /**
   * @param {HTMLElement} root
   * @param {object} config
   */
  function updateHint(root, config) {
    const hint = root.querySelector('[data-pds-delivery-hint]');
    if (!hint) return;

    const earliest = getEarliestDeliveryDate(config);
    hint.textContent = config.strings.hintEarliest.replace('[date]', formatYmd(earliest));
    hint.hidden = false;
  }

  /**
   * @param {HTMLElement} root
   * @param {string} message
   */
  function showFieldError(root, message) {
    const fieldError = root.querySelector('[data-pds-delivery-error]');
    if (fieldError) {
      fieldError.textContent = message;
      fieldError.hidden = !message;
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function clearFieldError(root) {
    showFieldError(root, '');
  }

  /**
   * @param {HTMLElement} root
   * @param {string} message
   */
  function showTimeFieldError(root, message) {
    const fieldError = root.querySelector('[data-pds-delivery-time-error]');
    if (fieldError) {
      fieldError.textContent = message;
      fieldError.hidden = !message;
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function clearTimeFieldError(root) {
    showTimeFieldError(root, '');
  }

  /**
   * @param {HTMLElement} root
   * @returns {{ dateSelect: HTMLSelectElement|null, timeSelect: HTMLSelectElement|null, timeFixed: HTMLInputElement|null }}
   */
  function getDeliveryFields(root) {
    return {
      dateSelect: root.querySelector('select[data-pds-delivery-date]'),
      timeSelect: root.querySelector('select[data-pds-delivery-time]'),
      timeFixed: root.querySelector('[data-pds-delivery-time-fixed]'),
      setAtHidden: root.querySelector('[data-pds-delivery-set-at]'),
    };
  }

  /**
   * @param {HTMLElement} root
   * @param {string} setAt
   */
  function syncSetAtHiddenField(root, setAt) {
    const { setAtHidden } = getDeliveryFields(root);
    if (setAtHidden) setAtHidden.value = setAt;
  }

  /**
   * @param {HTMLElement} root
   * @param {object} config
   * @param {boolean} updateSetAt
   * @returns {string}
   */
  function resolveSetAt(root, config, updateSetAt) {
    if (updateSetAt) return formatSetAtNow(config.timeZone);

    const { setAtHidden } = getDeliveryFields(root);
    return setAtHidden?.value || config.selectedSetAt || '';
  }

  /**
   * @param {HTMLElement} root
   * @param {object} config
   * @returns {string}
   */
  function getDeliveryTimeValue(root, config) {
    if (!isTimeSlotRequired(config)) {
      return getTimeNoneValue(config);
    }

    const { timeSelect } = getDeliveryFields(root);
    return timeSelect?.value || '';
  }

  /**
   * フォームの現在値をそのまま保存用属性にまとめる（セレクトの再生成なし）。
   * @param {HTMLElement} root
   * @param {object} config
   * @param {boolean} [refreshSetAt]
   * @returns {Record<string, string>}
   */
  function buildDeliveryAttributesForSave(root, config, refreshSetAt = true) {
    const setAt = refreshSetAt
      ? formatSetAtNow(config.timeZone)
      : resolveSetAt(root, config, false) || formatSetAtNow(config.timeZone);

    syncSetAtHiddenField(root, setAt);

    const { dateSelect } = getDeliveryFields(root);

    return {
      [config.dateAttributeKey]: dateSelect?.value || '',
      [config.timeAttributeKey]: getDeliveryTimeValue(root, config),
      [config.setAtAttributeKey]: setAt,
    };
  }

  /**
   * @param {object} config
   * @param {Record<string, string>} attributes
   */
  function applyAttributesToConfig(config, attributes) {
    if (attributes[config.dateAttributeKey]) {
      config.selectedDate = attributes[config.dateAttributeKey];
    }
    if (attributes[config.timeAttributeKey]) {
      config.selectedTime = attributes[config.timeAttributeKey];
    }
    if (attributes[config.setAtAttributeKey]) {
      config.selectedSetAt = attributes[config.setAtAttributeKey];
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {object} config
   * @param {{ updateSetAt?: boolean, preserveDate?: boolean }} [options]
   * @returns {Record<string, string>}
   */
  function buildDeliveryAttributes(root, config, options = {}) {
    const { updateSetAt = true, preserveDate = false } = options;
    const { dateSelect } = getDeliveryFields(root);
    const setAt = resolveSetAt(root, config, updateSetAt);

    if (setAt) syncSetAtHiddenField(root, setAt);

    if (preserveDate) {
      return buildDeliveryAttributesForSave(root, config, updateSetAt);
    }

    const dateValue = ensureEarliestDate(dateSelect, config, 'current', options.forceEarliest === true);

    return {
      [config.dateAttributeKey]: dateValue,
      [config.timeAttributeKey]: getDeliveryTimeValue(root, config),
      [config.setAtAttributeKey]: setAt || formatSetAtNow(config.timeZone),
    };
  }

  /**
   * @returns {string}
   */
  function getCartUpdateUrl() {
    return window.routes?.cart_update_url || '/cart/update.js';
  }

  /**
   * @returns {{ locationId: string, navision: string }}
   */
  function readCompanyLocationAttributeValues() {
    return {
      locationId: document.getElementById(COMPANY_LOCATION_ID_INPUT_ID)?.value?.trim() || '',
      navision: document.getElementById(NAVISION_INPUT_ID)?.value?.trim() || '',
    };
  }

  /**
   * B2B カートで拠点連携属性が必要な画面かどうか。
   * @returns {boolean}
   */
  function isCompanyLocationRequired() {
    return Boolean(
      document.getElementById(COMPANY_LOCATION_ATTRS_ID) ||
        document.getElementById(COMPANY_LOCATION_ID_INPUT_ID) ||
        document.getElementById(NAVISION_INPUT_ID) ||
        document.querySelector('pds-delivery-date')
    );
  }

  /**
   * @returns {string}
   */
  function getCompanyLocationRequiredMessage() {
    const el =
      document.getElementById(COMPANY_LOCATION_ATTRS_ID) ||
      document.getElementById(COMPANY_LOCATION_ID_INPUT_ID) ||
      document.getElementById(NAVISION_INPUT_ID);
    return (
      el?.dataset?.pdsCompanyLocationRequiredMessage?.trim() || COMPANY_LOCATION_REQUIRED_MESSAGE
    );
  }

  /**
   * cart_company_location_id / NAVISION が両方そろっているか検証する。
   * @returns {string|null} エラーメッセージ。問題なければ null
   */
  function validateCompanyLocationAttributes() {
    if (!isCompanyLocationRequired()) return null;

    const { locationId, navision } = readCompanyLocationAttributeValues();
    if (locationId && navision) return null;

    return getCompanyLocationRequiredMessage();
  }

  /**
   * カート画面の hidden から拠点連携用属性を取得する。
   * @returns {Record<string, string>}
   */
  function buildCompanyLocationAttributes() {
    const { locationId, navision } = readCompanyLocationAttributeValues();
    /** @type {Record<string, string>} */
    const attributes = {};

    if (locationId) {
      attributes[COMPANY_LOCATION_ID_ATTRIBUTE_KEY] = locationId;
    }
    if (navision) {
      attributes[NAVISION_ATTRIBUTE_KEY] = navision;
    }

    return attributes;
  }

  /**
   * カート属性を /cart/update.js で保存する。
   * 配送日更新時も拠点属性を併せて送り、チェックアウト前に属性が欠けるのを防ぐ。
   * @param {Record<string, string>} attributes
   * @returns {Promise<boolean>}
   */
  async function updateCartAttributes(attributes) {
    const payload = Object.fromEntries(
      Object.entries({
        ...buildCompanyLocationAttributes(),
        ...attributes,
      }).filter(([key, value]) => key && value != null && String(value) !== '')
    );

    if (!Object.keys(payload).length) return false;

    try {
      const response = await fetch(getCartUpdateUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes: payload }),
      });

      if (!response.ok) {
        console.error('Failed to update cart attributes.', response.status, await response.text());
        return false;
      }

      await response.json();
      return true;
    } catch (error) {
      console.error('Network error during cart update:', error);
      return false;
    }
  }

  /**
   * 連続変更時の競合を防ぐため直列で保存する。
   * @param {Record<string, string>} attributes
   * @returns {Promise<boolean>}
   */
  function queueCartAttributeUpdate(attributes) {
    const task = attributeUpdateChain.then(() => updateCartAttributes(attributes));
    attributeUpdateChain = task.catch(() => false);
    return task;
  }

  /**
   * 拠点連携用属性を /cart/update.js で保存する。
   * location_id / NAVISION が欠けている場合は保存しない。
   * @returns {Promise<boolean>}
   */
  function persistCompanyLocationAttributes() {
    if (validateCompanyLocationAttributes()) return Promise.resolve(false);

    const attributes = buildCompanyLocationAttributes();
    if (
      !attributes[COMPANY_LOCATION_ID_ATTRIBUTE_KEY] ||
      !attributes[NAVISION_ATTRIBUTE_KEY]
    ) {
      return Promise.resolve(false);
    }

    return queueCartAttributeUpdate(attributes);
  }

  /**
   * @param {HTMLElement} root
   * @param {object} config
   * @returns {Promise<boolean>}
   */
  async function persistDeliveryAttributes(root, config) {
    const attributes = buildDeliveryAttributesForSave(root, config, true);
    const saved = await queueCartAttributeUpdate(attributes);
    if (saved) applyAttributesToConfig(config, attributes);
    return saved;
  }

  /**
   * @param {HTMLElement} root
   * @param {object} config
   * @returns {Promise<boolean>}
   */
  async function updateAllDeliveryAttributes(root, config, options = {}) {
    const attributes = buildDeliveryAttributes(root, config, options);
    const saved = await queueCartAttributeUpdate(attributes);
    if (saved) applyAttributesToConfig(config, attributes);
    return saved;
  }

  /**
   * @param {HTMLElement} root
   * @param {object} config
   * @returns {Promise<boolean>}
   */
  async function applyDeliveryDefaults(root, config) {
    const { dateSelect, timeFixed } = getDeliveryFields(root);
    if (!dateSelect) return false;

    updateHint(root, config);
    ensureEarliestDate(dateSelect, config);

    if (timeFixed) {
      timeFixed.value = getTimeNoneValue(config);
    }

    const attributes = buildDeliveryAttributes(root, config);
    const saved = await queueCartAttributeUpdate(attributes);
    if (saved) applyAttributesToConfig(config, attributes);
    return saved;
  }

  /**
   * @param {SubmitEvent} event
   * @returns {boolean}
   */
  function isCheckoutSubmit(event) {
    const submitter = event.submitter;
    if (!submitter) return false;
    return submitter.name === 'checkout' || submitter.id === 'checkout';
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {HTMLElement|null}
   */
  function getCartErrorsEl(form) {
    const formId = form.getAttribute('id');
    return (
      document.getElementById('cart-errors') ||
      (formId === 'CartDrawer-Form' ? document.getElementById('CartDrawer-CartErrors') : null)
    );
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} message
   */
  function showCartErrors(form, message) {
    const errorEl = getCartErrorsEl(form);
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.focus?.();
    }
  }

  /**
   * @param {HTMLFormElement} form
   */
  function clearCartErrors(form) {
    const errorEl = getCartErrorsEl(form);
    if (errorEl) errorEl.textContent = '';
  }

  /**
   * @returns {object|null}
   */
  function readDeliveryConfig() {
    const root = document.querySelector('pds-delivery-date');
    if (!root) return null;
    return readConfigForRoot(root);
  }

  let checkoutValidationBound = false;
  let skipCheckoutDeliveryGuard = false;

  /**
   * チェックアウト送信時に検証し、属性を保存してからフォームを送信する。
   * 配送日 UI が無い場合でも拠点属性は /cart/update.js で保存する。
   * cart_company_location_id / NAVISION 未設定時はエラーにして遷移させない。
   */
  function bindCheckoutValidation() {
    if (checkoutValidationBound) return;
    checkoutValidationBound = true;

    ['cart', 'CartDrawer-Form'].forEach((formId) => {
      const form = document.getElementById(formId);
      if (!form) return;

      form.addEventListener('submit', async (event) => {
        if (!isCheckoutSubmit(event)) return;

        if (skipCheckoutDeliveryGuard) {
          skipCheckoutDeliveryGuard = false;
          return;
        }

        const root = document.querySelector('pds-delivery-date');
        const config = readDeliveryConfig();
        const hasDeliveryGuard = Boolean(root && config);
        const requiresCompanyLocation = isCompanyLocationRequired();

        if (!hasDeliveryGuard && !requiresCompanyLocation) return;

        clearCartErrors(form);

        const locationError = validateCompanyLocationAttributes();
        if (locationError) {
          event.preventDefault();
          event.stopPropagation();
          showCartErrors(form, locationError);
          return;
        }

        if (hasDeliveryGuard) {
          const message = validateForCheckout(root, config);
          if (message) {
            event.preventDefault();
            event.stopPropagation();
            showCartErrors(form, message);
            return;
          }
        }

        event.preventDefault();
        event.stopPropagation();

        let saved = true;
        if (hasDeliveryGuard) {
          saved = await updateAllDeliveryAttributes(root, config, {
            updateSetAt: false,
            preserveDate: true,
          });
          if (!saved) {
            showCartErrors(form, config.strings.updateFailed || CART_ATTRIBUTE_UPDATE_FAILED_MESSAGE);
            return;
          }
        } else {
          saved = await persistCompanyLocationAttributes();
          if (!saved) {
            showCartErrors(form, CART_ATTRIBUTE_UPDATE_FAILED_MESSAGE);
            return;
          }
        }

        const submitter = event.submitter;
        skipCheckoutDeliveryGuard = true;
        if (submitter) {
          form.requestSubmit(submitter);
        } else {
          form.submit();
        }
      });
    });
  }

  /**
   * @param {HTMLElement} root
   * @param {object} config
   * @returns {string|null}
   */
  function validateForCheckout(root, config) {
    const { dateSelect } = getDeliveryFields(root);
    if (!dateSelect) return null;

    const setAt = resolveSetAt(root, config, false);
    const validationConfig = {
      ...config,
      selectedSetAt: setAt || config.selectedSetAt,
    };

    const dateMessage = validateSelectedDate(dateSelect.value, validationConfig, 'setAt');
    if (dateMessage) {
      showFieldError(root, dateMessage);
      return dateMessage;
    }
    clearFieldError(root);

    const timeMessage = validateSelectedTime(getDeliveryTimeValue(root, config), config);
    if (timeMessage) {
      showTimeFieldError(root, timeMessage);
      return timeMessage;
    }
    clearTimeFieldError(root);

    return null;
  }

  /**
   * @param {HTMLElement} root
   * @returns {object|null}
   */
  function readConfigForRoot(root) {
    const sectionId = root.dataset.sectionId;
    if (!sectionId) return null;

    const configEl =
      root.querySelector(`script[id="PdsDeliveryDateConfig-${sectionId}"]`) ||
      root.querySelector('script[id^="PdsDeliveryDateConfig-"]') ||
      document.getElementById(`PdsDeliveryDateConfig-${sectionId}`);
    if (!configEl) return null;

    try {
      const config = JSON.parse(configEl.textContent || '{}');
      config.timeZone = config.timeZone || 'Asia/Tokyo';
      config.cutoffHour = config.cutoffHour ?? 13;
      config.dateAttributeKey = config.dateAttributeKey || DATE_ATTRIBUTE_KEY_DEFAULT;
      config.timeAttributeKey = config.timeAttributeKey || TIME_ATTRIBUTE_KEY_DEFAULT;
      config.setAtAttributeKey = config.setAtAttributeKey || SET_AT_ATTRIBUTE_KEY_DEFAULT;
      config.timeNoneValue = config.timeNoneValue || TIME_NONE_DEFAULT;
      config.deliverytimeMetafield =
        config.deliverytimeMetafield != null ? String(config.deliverytimeMetafield).trim() : '';
      config.timeSlotItemCountMax = config.timeSlotItemCountMax ?? 5;
      config.showTimeSlot = resolveShowTimeSlot(config);
      config.datePickerMode = config.datePickerMode === 'select' ? 'select' : 'calendar';
      config.locationId = config.locationId != null ? String(config.locationId) : '';
      return config;
    } catch {
      return null;
    }
  }

  /**
   * @param {HTMLElement|null} root
   * @returns {{ locationId: string, leadtime: string, showTimeSlot: boolean }|null}
   */
  function getDeliveryContextFromRoot(root) {
    const config = readConfigForRoot(root);
    if (!config) return null;

    return {
      locationId: String(config.locationId || ''),
      leadtime: String(config.leadtime || '').trim(),
      showTimeSlot: config.showTimeSlot === true,
    };
  }

  /**
   * @param {{ locationId: string, leadtime: string, showTimeSlot: boolean }|null} left
   * @param {{ locationId: string, leadtime: string, showTimeSlot: boolean }|null} right
   * @returns {boolean}
   */
  function deliveryContextsDiffer(left, right) {
    if (!left || !right) return Boolean(left) !== Boolean(right);

    return (
      left.locationId !== right.locationId ||
      left.leadtime !== right.leadtime ||
      left.showTimeSlot !== right.showTimeSlot
    );
  }

  /**
   * @returns {string}
   */
  function getPageCompanyLocationId() {
    return document.getElementById('cart-attributes-cart_company_location_id')?.value?.trim() || '';
  }

  /**
   * @param {string} [pageLocationId]
   * @returns {Promise<{ newRoot: Element|null, nextContext: object|null }>}
   */
  async function fetchFreshDeliveryDateRoot(pageLocationId = '') {
    const cartUrl = window.routes?.cart_url || '/cart';
    const footer = document.getElementById('main-cart-footer');
    const cacheBust = Date.now();

    const fetchDoc = async (url) => {
      const resp = await fetch(url, {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!resp.ok) return null;
      return new DOMParser().parseFromString(await resp.text(), 'text/html');
    };

    let doc = null;
    if (footer?.dataset?.id) {
      doc = await fetchDoc(`${cartUrl}?section_id=${footer.dataset.id}&_=${cacheBust}`);
    }

    let newRoot = doc?.querySelector('pds-delivery-date') || null;
    let nextContext = getDeliveryContextFromRoot(newRoot);
    const sectionMatchesPage =
      !pageLocationId || !nextContext?.locationId || nextContext.locationId === pageLocationId;

    if (pageLocationId && newRoot && !sectionMatchesPage) {
      doc = await fetchDoc(`${cartUrl}?_=${cacheBust + 1}`);
      newRoot = doc?.querySelector('pds-delivery-date') || newRoot;
      nextContext = getDeliveryContextFromRoot(newRoot);
    }

    return { newRoot, nextContext };
  }

  /**
   * Section API から最新の配送日 UI を取得し、拠点・leadtime が異なる場合のみ差し替える。
   * @param {HTMLElement} root
   * @returns {Promise<HTMLElement|null>}
   */
  async function syncDeliveryDateRootFromFooterSection(root) {
    const footer = document.getElementById('main-cart-footer');
    if (!footer?.dataset?.id || !root?.isConnected) return root;

    const currentContext = getDeliveryContextFromRoot(root);
    const pageLocationId = getPageCompanyLocationId();
    const widgetLocationId = root.dataset.locationId || currentContext?.locationId || '';
    const locationMismatch = Boolean(
      pageLocationId && widgetLocationId && pageLocationId !== widgetLocationId
    );

    try {
      const { newRoot, nextContext } = await fetchFreshDeliveryDateRoot(pageLocationId);

      const contextMismatch =
        currentContext && nextContext && deliveryContextsDiffer(currentContext, nextContext);

      if (!locationMismatch && !contextMismatch) return root;

      resetDeliveryDateRoot(root);

      if (!newRoot) {
        root.remove();
        return null;
      }

      const imported = document.importNode(newRoot, true);
      imported.dataset.pdsDeliverySkipConnectInit = 'true';
      root.replaceWith(imported);
      imported.dataset.pdsDeliveryContextChanged = 'true';
      return imported;
    } catch (error) {
      console.error('Failed to sync delivery date from cart footer section.', error);
      return root;
    }
  }

  /**
   * @param {HTMLElement} root
   */
  async function initDeliveryDate(root) {
    if (root.dataset.pdsDeliveryDateInit === 'true' || root.dataset.pdsDeliveryDateInit === 'syncing') {
      return;
    }

    root.dataset.pdsDeliveryDateInit = 'syncing';

    const syncedRoot = await syncDeliveryDateRootFromFooterSection(root);
    if (!syncedRoot) {
      return;
    }

    root = syncedRoot;
    const contextChanged = root.dataset.pdsDeliveryContextChanged === 'true';
    root.dataset.pdsDeliveryContextChanged = '';

    const config = readConfigForRoot(root);
    if (!config) {
      root.dataset.pdsDeliveryDateInit = '';
      return;
    }

    root.dataset.pdsDeliveryDateInit = 'true';

    const pageLocationId = getPageCompanyLocationId();
    const widgetLocationId = root.dataset.locationId || config.locationId || '';
    const locationChanged = Boolean(
      pageLocationId && widgetLocationId && pageLocationId !== widgetLocationId
    );
    const forceEarliest = contextChanged || locationChanged;

    const { dateSelect, timeSelect } = getDeliveryFields(root);
    if (!dateSelect) {
      root.dataset.pdsDeliveryDateInit = '';
      return;
    }

    updateHint(root, config);
    populateDateSelect(dateSelect, config);

    config.datePickerMode = config.datePickerMode === 'select' ? 'select' : 'calendar';

    const deliveryApi = {
      config,
      parseYmd,
      formatYmd,
      formatDisplayDate,
      addCalendarDays,
      getSelectableRange,
      getHolidaySet,
      isDeliverable,
      setDateSelectValue,
    };

    if (typeof window.PdsDeliveryCalendar !== 'undefined' && window.PdsDeliveryCalendar.useCalendarPicker(deliveryApi)) {
      root.dataset.datePickerMode = 'calendar';
      window.PdsDeliveryCalendar.init(root, config, dateSelect, deliveryApi);
    } else {
      root.dataset.datePickerMode = 'select';
    }

    if (config.selectedDate && !forceEarliest) {
      setDateSelectValue(dateSelect, config.selectedDate);
    }
    if (isTimeSlotRequired(config) && config.selectedTime && timeSelect) {
      timeSelect.value = config.selectedTime;
    }
    if (config.selectedSetAt) {
      syncSetAtHiddenField(root, config.selectedSetAt);
    }

    if (!root.dataset.pdsDeliveryListenersBound) {
      root.dataset.pdsDeliveryListenersBound = 'true';

      const onDateChange = async () => {
        const message = validateSelectedDate(dateSelect.value, config, 'setAt');
        if (message) {
          showFieldError(root, message);
          return;
        }

        clearFieldError(root);
        const saved = await persistDeliveryAttributes(root, config);
        if (!saved) {
          showFieldError(root, config.strings.updateFailed);
        }
      };

      const onTimeChange = async () => {
        if (!isTimeSlotRequired(config) || !timeSelect) return;

        const message = validateSelectedTime(timeSelect.value, config);
        if (message) {
          showTimeFieldError(root, message);
          return;
        }

        clearTimeFieldError(root);
        const saved = await persistDeliveryAttributes(root, config);
        if (!saved) {
          showTimeFieldError(root, config.strings.updateFailed);
        }
      };

      dateSelect.addEventListener('change', onDateChange);
      timeSelect?.addEventListener('change', onTimeChange);
    }

    bindCheckoutValidation();

    if (contextChanged || locationChanged) {
      root.dataset.pdsDeliveryUserPicked = '';
    }

    const savedDate = forceEarliest ? '' : config.selectedDate || dateSelect.value;
    const isStale =
      forceEarliest ||
      !savedDate ||
      !config.selectedSetAt ||
      validateSelectedDate(savedDate, config, 'current');

    if (isStale) {
      applyDeliveryDefaults(root, config, { forceEarliest }).then((saved) => {
        if (root.dataset.pdsDeliveryUserPicked === 'true') return;

        if (!saved) {
          showFieldError(root, config.strings.updateFailed);
        } else if (typeof root.pdsDeliveryCalendarRefresh === 'function') {
          root.pdsDeliveryCalendarRefresh();
        }
      });
    } else if (typeof root.pdsDeliveryCalendarRefresh === 'function') {
      root.pdsDeliveryCalendarRefresh();
    }
  }

  class PdsDeliveryDate extends HTMLElement {
    connectedCallback() {
      if (this.dataset.pdsDeliverySkipConnectInit === 'true') {
        this.dataset.pdsDeliverySkipConnectInit = '';
        return;
      }
      initDeliveryDate(this);
    }
  }

  if (!customElements.get('pds-delivery-date')) {
    customElements.define('pds-delivery-date', PdsDeliveryDate);
  }

  function initAllDeliveryDates() {
    document.querySelectorAll('pds-delivery-date').forEach((root) => initDeliveryDate(root));
    persistCompanyLocationAttributes();
    bindCheckoutValidation();
  }

  /**
   * @param {HTMLElement} root
   */
  function resetDeliveryDateRoot(root) {
    root.dataset.pdsDeliveryDateInit = '';
    root.dataset.pdsDeliveryListenersBound = '';
    root.dataset.pdsDeliveryUserPicked = '';
    const calendar = root.querySelector('[data-pds-delivery-calendar]');
    if (calendar) {
      if (calendar.pdsCalendarDocumentClick) {
        document.removeEventListener('click', calendar.pdsCalendarDocumentClick);
        calendar.pdsCalendarDocumentClick = null;
      }
      calendar.dataset.pdsCalendarInit = '';
    }
  }

  async function reloadAllDeliveryDates() {
    document.querySelectorAll('pds-delivery-date').forEach((root) => resetDeliveryDateRoot(root));
    await Promise.all(Array.from(document.querySelectorAll('pds-delivery-date')).map((root) => initDeliveryDate(root)));
  }

  /**
   * カート数量変更で showTimeSlot（NULL かつ item_count 閾値）が変わったときだけ Section API で差し替える。
   * @param {{ cartData?: { item_count?: number } }} [event]
   * @returns {Promise<void>}
   */
  async function syncDeliveryDatesOnCartQuantityChange(event = {}) {
    const roots = Array.from(document.querySelectorAll('pds-delivery-date'));
    if (!roots.length) return;

    const itemCount = event.cartData?.item_count;

    for (const root of roots) {
      if (!root.isConnected) continue;

      const config = readConfigForRoot(root);
      if (!config) continue;

      const currentShow = config.showTimeSlot === true;
      const nextShow = resolveShowTimeSlot(config, itemCount);
      if (nextShow === currentShow) continue;

      const syncedRoot = await syncDeliveryDateRootFromFooterSection(root);
      if (!syncedRoot?.isConnected) continue;

      if (syncedRoot.dataset.pdsDeliveryContextChanged === 'true') {
        syncedRoot.dataset.pdsDeliveryDateInit = '';
        await initDeliveryDate(syncedRoot);
      }
    }
  }

  window.PdsDeliveryDate = {
    reload: reloadAllDeliveryDates,
    resetRoot: resetDeliveryDateRoot,
    getContextFromRoot: getDeliveryContextFromRoot,
    contextsDiffer: deliveryContextsDiffer,
    syncOnCartQuantityChange: syncDeliveryDatesOnCartQuantityChange,
    resolveShowTimeSlot,
  };

  document.addEventListener('DOMContentLoaded', initAllDeliveryDates);
  document.addEventListener('shopify:section:load', initAllDeliveryDates);

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;

    document.querySelectorAll('pds-delivery-date').forEach((root) => {
      resetDeliveryDateRoot(root);
    });
    initAllDeliveryDates();
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const source = event && event.source;
      if (source === 'pds-delivery-date') {
        return;
      }

      if (source === 'pds-attached-gifts' || source === 'cart-items') {
        syncDeliveryDatesOnCartQuantityChange(event);
        return;
      }

      reloadAllDeliveryDates();
    });
  }
})();
